import React, { useEffect, useRef, useState } from "react";
import { Feed } from "./Feed/Feed";
import { handleImages } from "./utils/handleImages";
import { handleAttributes } from "./utils/handleAttributes";
import styles from "./Insta.module.scss";
import { InstagramOptionsType } from "@/types/ModuleOptionsType";
import { OptionsSocialMediaType } from "@/types/OptionType";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { useLenis } from "lenis/react";
import { AddProperty } from "@/components/PopUp/PopUp";
import { useAppContext } from "@/context/AppContext";
import { Modul_Instagram } from "@/wp/types/acf-module-types";
import { Section } from "@/components/Section/Section";
import { Headline } from "@/components/Headline/Headline";
import { FadeInUp } from "@/components/Animation/ScrollAnimation";
import { CustomLink } from "@/components/CustomLink";
import { Button } from "@/components/Button/Button";
import useWindowSize from "@/hooks/useWindowSize";
import useUpdateEffect from "@/hooks/useUpdateEffect";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { ContentBlockerWrapper } from "@aleksundshantu/aunds-cookie-plugin-react";
import { useRouter } from "next/router";

export type InstaModuleType = Modul_Instagram;

interface InstaProps {
  data: InstaModuleType;
  feed: string;
  options: InstagramOptionsType;
  socialMedia: OptionsSocialMediaType;
}

export default function Insta({
  data,
  options,
  socialMedia,
  feed,
}: InstaProps) {
  const { follow_button_type, show_loadmore } = options;
  const {
    background_color_class,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
  } = data.layout;
  const { headline, headline_html_tag } = data.instagram || {};
  const { link, icon } = (socialMedia.platforms &&
    socialMedia.platforms.filter(
      (platform) => platform.name?.toLowerCase() === "instagram",
    )[0]) || { link: "#" };

  const { getLabel } = useGlobalContext();
  const router = useRouter();
  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  return (
    <Section
      className={`${background_color_class} ${section_padding_class} ${section_margin_class} ${styles.instagram}`}
      disableColumns
    >
      {follow_button_type !== "primary-bottom" ? (
        <FadeInUp
          className={`${styles.headline} ${follow_button_type === "secondary-centered" ? styles.centered : ""}`}
        >
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            disableAnimation
            // extraClass={headline_text_align_class}
          />
          <CustomLink
            href={link}
            target="_blank"
            className={styles["follow-btn"]}
          >
            {icon && icon.url && (
              <CustomSVG
                width={20}
                height={20}
                src={icon.url}
                wrapper={"span"}
                aria-hidden="true"
                className="is-flex"
              />
            )}
            <span>{getLabel("follow")}</span>
          </CustomLink>
        </FadeInUp>
      ) : (
        <FadeInUp>
          <Headline
            headline_html_tag={headline_html_tag}
            headline={headline}
            extraClass={headline_text_align_class}
            disableAnimation
          />
        </FadeInUp>
      )}
      <ContentBlockerWrapper
        platformName={"instagram"}
        wpApiUrl={process.env.WP_API_URL!}
        locale={router?.locale !== "de" ? router?.locale : undefined}
        extraClassName={{ wrapper: `${styles["insta-blocker"]}` }}
      >
        <FadeInUp>
          <InstaFeed feed={feed} options={options} followUrl={link} />
        </FadeInUp>
      </ContentBlockerWrapper>

      {!show_loadmore && follow_button_type === "primary-bottom" && link && (
        <FadeInUp className={``}>
          <Button
            type="primary"
            href={link}
            target="_blank"
            className="mx-auto"
          >
            {getLabel("follow_on_instagram")}
          </Button>
        </FadeInUp>
      )}
    </Section>
  );
}

interface InstaFeedProps {
  feed: string;
  options: InstagramOptionsType;
  followUrl?: string;
}

export const InstaFeed = ({ feed, options, followUrl }: InstaFeedProps) => {
  const { getLabel } = useGlobalContext();

  const baseContainerRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const imgCount = width < 768 ? 4 : width < 1024 ? 3 : width < 1920 ? 4 : 5;
  // number of how many posts will be loaded on load more click.
  const loadCount = imgCount;
  // number of initial videos displayed in the feed. (e.g. 6)
  const initialCount = imgCount;

  const [loadMore, setLoadMore] = useState<boolean>(true);
  const [imageCount, setImageCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [images, setImages] = useState<any[]>([]);

  const imageLoader = () => {
    const htmlDoc = new DOMParser().parseFromString(feed, "text/html");
    const base = htmlDoc.body.querySelector("#sb_instagram");

    if (base) {
      handleAttributes(base, baseContainerRef);
      base.removeAttribute("class");
      base.removeAttribute("style");
    }

    const images = base?.querySelector("#sbi_images");

    const imagesLength = images?.children.length;
    if (imagesLength && imageCount >= imagesLength - initialCount) {
      setLoadMore(false);
    } else {
      setLoadMore(true);
    }

    handleImages(images, initialCount, imageCount, loadCount, setImages);
  };

  useEffect(() => {
    setImages([]);
    setImageCount(0);
    imageLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCount]);

  useUpdateEffect(() => {
    imageLoader();
  }, [imageCount]);

  const { instagramModal } = useAppContext();
  const lenis = useLenis();
  useEffect(() => {
    const htmlTag = document.documentElement;
    const scrollBarWidth = window.innerWidth - htmlTag.clientWidth;
    const tagsToAddmargin = document.getElementsByClassName(
      "scrollbar-width",
    ) as HTMLCollectionOf<HTMLElement>;
    if (instagramModal) {
      if (scrollBarWidth !== 0) {
        AddProperty(
          tagsToAddmargin,
          "width",
          `calc(100% - ${scrollBarWidth}px)`,
        );
        htmlTag.style.marginRight = `${scrollBarWidth}px`;
      }
      lenis?.stop();
    } else {
      AddProperty(tagsToAddmargin, "width", `100%`);
      htmlTag.style.marginRight = `unset`;
      lenis?.start();
    }
  }, [instagramModal, lenis]);

  return (
    <>
      <div className="feed-wrapper">
        <div ref={baseContainerRef}>
          {images.length > 0 ? (
            <Feed
              props={{
                images,
                loadMore,
                loadCount,
                loadMoreText: getLabel("load_more_label", "Load more"),
                setImageCount,
                followUrl,
                options,
                followText: getLabel(
                  "follow_on_instagram",
                  "Follow on Instagram",
                ),
              }}
            />
          ) : (
            <p>{getLabel("no_posts_instagram")}</p>
          )}
        </div>
      </div>
      {/* <AnimatePresence>
        {modal && (
          <motion.div
            variants={clip_circle_top_variant}
            initial={"initial"}
            animate={"animate"}
            exit={"exit"}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 9999,
              width: "100%",
              height: "100%",
            }}
            className={styles["insta-modal-wrapper"] + " scrollbar-width"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setModal(null);
            }}
          >
            <Modal modal={modal} setModal={setModal} />
          </motion.div>
        )}
      </AnimatePresence> */}
    </>
  );
};
